/**
 * ACT-1237 (AC-ADM-4): Usage tracking dashboard.
 *
 * Queries care-management-gql's usageStats (App Insights KQL proxy) for WAU
 * and feature-usage breakdowns by persona or IPA-as-clinic surrogate.
 * Defaults to trailing 8 weeks, groupBy PERSONA.
 *
 * The query runs on the ambient Member360ApolloProvider client (same as the
 * other admin pages), fetchPolicy "cache-first" with previousData kept during
 * refetch so the chart doesn't flash blank on filter changes.
 *
 * Event emission (member_search_executed, member_profile_opened, etc.) lives
 * in the AstraConnect app (the member-360-standalone repo); this dashboard
 * only emits admin_page_viewed for itself.
 *
 * PII rule (ACT-1037): no member PHI — only opaque persona/ipa codes.
 */
import { useState, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { BarChart2, Download, RefreshCw } from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import LoadingSpinner from "../../components/LoadingSpinner";
import { AdminWall } from "../components/AdminWall";
import type { ApolloError } from "@apollo/client";
import {
  USAGE_STATS_QUERY,
  type UsageStatsResult,
  type UsageStatsVars,
  type UsageGroupBy,
  type UsageStatsRow,
} from "../graphql/adminOps";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** ISO date string for N weeks ago from today, normalised to Monday. */
function weeksAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n * 7);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Escape a single CSV field against both column corruption and formula
 * injection:
 *   - Always double-quote the field and double any internal quotes (handles
 *     commas/newlines/quotes safely, per RFC 4180).
 *   - If the value begins with `= + - @` (or a tab/CR, which some parsers
 *     also treat as formula-triggering), prefix it with a leading `'` so
 *     Excel/Sheets renders it as literal text instead of evaluating it as a
 *     formula when an admin opens the exported file.
 */
function escapeCsvField(value: string | number): string {
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }
  return `"${str.replace(/"/g, '""')}"`;
}

function csvRow(fields: (string | number)[]): string {
  return fields.map(escapeCsvField).join(",");
}

/** Inline CSV export — no extra lib. Satisfies AC "export the underlying data". */
function convertToCSV(rows: UsageStatsRow[]): string {
  const header = csvRow([
    "weekStart",
    "dimension",
    "dimensionValue",
    "activeUsers",
    "eventCount",
  ]);
  const lines = rows.map((r) =>
    csvRow([r.weekStart, r.dimension, r.dimensionValue, r.activeUsers, r.eventCount]),
  );
  return [header, ...lines].join("\n");
}

function downloadCSV(rows: UsageStatsRow[]): void {
  const csv = convertToCSV(rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `atlas-copilot-usage-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Chart data transforms
// ---------------------------------------------------------------------------

/**
 * WAU-over-weeks: one series per dimensionValue, x = weekStart (short date),
 * y = activeUsers.
 */
function toWauChartData(
  rows: UsageStatsRow[],
): { week: string; [dim: string]: number | string }[] {
  const weekMap = new Map<string, Record<string, number>>();
  for (const row of rows) {
    const weekLabel = row.weekStart.slice(0, 10);
    const bucket = weekMap.get(weekLabel) ?? {};
    bucket[row.dimensionValue] = (bucket[row.dimensionValue] ?? 0) + row.activeUsers;
    weekMap.set(weekLabel, bucket);
  }
  return Array.from(weekMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([week, dims]) => ({ week, ...dims }));
}

/**
 * Feature-usage-by-dimension: one bar per dimensionValue, y = total eventCount.
 */
function toUsageBarData(rows: UsageStatsRow[]): { name: string; events: number }[] {
  const dimMap = new Map<string, number>();
  for (const row of rows) {
    dimMap.set(row.dimensionValue, (dimMap.get(row.dimensionValue) ?? 0) + row.eventCount);
  }
  return Array.from(dimMap.entries())
    .sort(([, a], [, b]) => b - a)
    .map(([name, events]) => ({ name, events }));
}

// ---------------------------------------------------------------------------
// Chart colours (Astrana brand palette, accessible contrast)
// ---------------------------------------------------------------------------
const SERIES_COLORS = [
  "hsl(324,80%,40%)",
  "hsl(210,70%,50%)",
  "hsl(160,60%,40%)",
  "hsl(38,90%,50%)",
  "hsl(270,60%,55%)",
  "hsl(0,65%,50%)",
];

// ---------------------------------------------------------------------------
// Known event names — used in the event-type filter dropdown
// ---------------------------------------------------------------------------
const KNOWN_EVENTS = [
  { value: "", label: "All events" },
  { value: "member_search_executed", label: "Member search" },
  { value: "member_profile_opened", label: "Member profile opened" },
  { value: "member_tab_viewed", label: "Member tab viewed" },
  { value: "ai_assistant_message_sent", label: "AI assistant message" },
  { value: "admin_page_viewed", label: "Admin page viewed" },
  { value: "session_started", label: "Session started" },
  { value: "session_expired", label: "Session expired" },
];

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function UsagePage() {
  const [groupBy, setGroupBy] = useState<UsageGroupBy>("PERSONA");
  const [startDate, setStartDate] = useState<string>(weeksAgo(8));
  const [endDate, setEndDate] = useState<string>(today());
  const [eventName, setEventName] = useState<string>("");
  const [emailFilter, setEmailFilter] = useState<string>("");

  // Default env filter to the current deployment environment so the dashboard
  // only shows data for this env (dev/qa/uat/prod all share one App Insights
  // resource; filtering by env is required for meaningful dashboards).
  const currentEnv = import.meta.env.VITE_ENVIRONMENT as string | undefined;

  const { data, previousData, loading, error: apolloError, refetch } = useQuery<
    UsageStatsResult,
    UsageStatsVars
  >(USAGE_STATS_QUERY, {
    variables: {
      input: {
        startDate,
        endDate,
        groupBy,
        eventName: eventName || undefined,
        env: currentEnv || undefined,
        userEmail: emailFilter || undefined,
      },
    },
    fetchPolicy: "cache-first",
    // Keep previous data visible while the new query is in-flight so charts
    // don't flash blank on filter changes.
    notifyOnNetworkStatusChange: true,
  });

  // Show previous data while refetching so UX stays populated.
  const activeData = data ?? previousData;
  const rows = activeData?.usageStats?.rows ?? [];
  const totals = activeData?.usageStats;
  // isConfigured is false when the backend has no App Insights connection string
  // for the current env — show an informational notice instead of empty charts.
  const isConfigured = activeData?.usageStats?.isConfigured ?? true;

  // Non-auth errors (network, unexpected) shown inline.
  const nonAuthError: ApolloError | undefined =
    apolloError && !apolloError.graphQLErrors.length ? apolloError : undefined;

  const wauData = toWauChartData(rows);
  const usageBarData = toUsageBarData(rows);

  // Unique dimension values for chart series.
  const dimValues = Array.from(new Set(rows.map((r) => r.dimensionValue)));

  const handleExport = useCallback(() => {
    downloadCSV(rows);
  }, [rows]);

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-primary" aria-hidden="true" />
          <h2 className="text-xl font-semibold text-foreground m-0">Usage</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={loading}
            aria-label="Refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={rows.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" aria-hidden="true" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4 flex flex-wrap gap-4 items-end">
        {!currentEnv && (
          <div className="w-full flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-400/40 text-xs text-amber-800 dark:text-amber-200">
            <span className="font-semibold">All environments</span>
            <span className="text-amber-700 dark:text-amber-300">
              — VITE_ENVIRONMENT is unset; data aggregates across all envs sharing this App Insights resource.
            </span>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="usage-start">
            From
          </label>
          <input
            id="usage-start"
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="h-8 px-2 rounded-md border border-border bg-background text-sm text-foreground"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="usage-end">
            To
          </label>
          <input
            id="usage-end"
            type="date"
            value={endDate}
            min={startDate}
            max={today()}
            onChange={(e) => setEndDate(e.target.value)}
            className="h-8 px-2 rounded-md border border-border bg-background text-sm text-foreground"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="usage-groupby">
            Group by
          </label>
          <select
            id="usage-groupby"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as UsageGroupBy)}
            className="h-8 px-2 rounded-md border border-border bg-background text-sm text-foreground"
          >
            <option value="PERSONA">Persona</option>
            <option value="CLINIC">Clinic (IPA)</option>
            <option value="USER">User (email)</option>
            <option value="TAB">Tab</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="usage-event">
            Event type
          </label>
          <select
            id="usage-event"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            className="h-8 px-2 rounded-md border border-border bg-background text-sm text-foreground"
          >
            {KNOWN_EVENTS.map((ev) => (
              <option key={ev.value} value={ev.value}>
                {ev.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground" htmlFor="usage-email">
            User email
          </label>
          <input
            id="usage-email"
            type="email"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            placeholder="Filter by email…"
            className="h-8 px-2 rounded-md border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/*
        Auth-wall: intercepts UNAUTHENTICATED/FORBIDDEN from connect-gql.
        Wraps the entire content region (not a sibling) so an auth error
        replaces the charts/notice/table instead of rendering above them —
        otherwise the user would see both the lock screen AND the content
        below it (e.g. the "telemetry not configured" empty state).
      */}
      <AdminWall error={apolloError} screenLabel="Usage">
        {/* isConfigured=false: backend has no App Insights connection string for this env */}
        {!isConfigured && (
          <div
            role="status"
            className="rounded-xl border border-amber-400/40 bg-amber-50 dark:bg-amber-900/20 px-5 py-4 text-sm text-amber-800 dark:text-amber-200"
          >
            <p className="font-semibold m-0 mb-1">Usage telemetry is not configured for this environment</p>
            <p className="m-0">
              The backend has no App Insights connection string for{" "}
              <code className="font-mono">{currentEnv ?? "(none)"}</code>. Set{" "}
              <code className="font-mono">VITE_APPINSIGHTS_CONNECTION_STRING</code> and the
              corresponding backend env var, then redeploy.
            </p>
          </div>
        )}

        {/* Initial load spinner — hidden once we have any data (previous or fresh) */}
        {loading && !activeData && (
          <LoadingSpinner label="Loading usage data…" fullScreen={false} />
        )}

        {/* Non-auth network errors */}
        {nonAuthError && (
          <div
            role="alert"
            className="rounded-xl border border-destructive/40 bg-destructive/10 px-5 py-4 text-sm text-destructive"
          >
            <p className="font-semibold m-0 mb-1">Failed to load usage data</p>
            <p className="m-0">{nonAuthError.message}</p>
          </div>
        )}

        {/* Totals summary */}
        {totals && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-xs text-muted-foreground m-0">Total active users</p>
              <p className="text-2xl font-bold text-foreground m-0">
                {totals.totalActiveUsers.toLocaleString()}
              </p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-xs text-muted-foreground m-0">Total events</p>
              <p className="text-2xl font-bold text-foreground m-0">
                {totals.totalEvents.toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {rows.length === 0 && !loading && (
          <div className="bg-card border border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
            No usage data found for the selected filters.
          </div>
        )}

        {rows.length > 0 && (
          <>
            {/* WAU over weeks — line chart */}
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground m-0 mb-4">
                Weekly active users by{" "}
                {groupBy === "PERSONA" ? "persona" : groupBy === "CLINIC" ? "clinic" : groupBy === "USER" ? "user" : "tab"}
              </h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={wauData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  {dimValues.map((dim, i) => (
                    <Line
                      key={dim}
                      type="monotone"
                      dataKey={dim}
                      stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Feature usage by dimension — bar chart */}
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground m-0 mb-4">
                Total events by{" "}
                {groupBy === "PERSONA" ? "persona" : groupBy === "CLINIC" ? "clinic" : groupBy === "USER" ? "user" : "tab"}
              </h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={usageBarData}
                  margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="events" fill={SERIES_COLORS[0]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Results table */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground m-0">
                  Row data ({rows.length} rows)
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                        Week start
                      </th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                        Dimension
                      </th>
                      <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                        Value
                      </th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">
                        Active users
                      </th>
                      <th className="px-4 py-2.5 text-right text-xs font-medium text-muted-foreground">
                        Events
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr
                        key={`${row.weekStart}-${row.dimensionValue}-${i}`}
                        className="border-b border-border last:border-0 hover:bg-accent/40 transition-colors"
                      >
                        <td className="px-4 py-2.5 text-foreground">{row.weekStart.slice(0, 10)}</td>
                        <td className="px-4 py-2.5 text-muted-foreground capitalize">
                          {row.dimension}
                        </td>
                        <td className="px-4 py-2.5 text-foreground font-mono text-xs">
                          {row.dimensionValue}
                        </td>
                        <td className="px-4 py-2.5 text-right text-foreground">
                          {row.activeUsers.toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5 text-right text-foreground">
                          {row.eventCount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </AdminWall>
    </div>
  );
}
